import type { Lang } from "../types.ts";
import type { Settings } from "../state/store.ts";
import { LANGS, t } from "../i18n/ui.ts";

interface Props {
  settings: Settings;
  onChange: (patch: Partial<Settings>) => void;
  onSwap: () => void;
}

/**
 * Primary/secondary language pickers. The secondary language is what makes the
 * parallel view work, so it is a first-class control in the header rather than
 * buried in a settings page.
 */
export function LanguageControls({ settings, onChange, onSwap }: Props) {
  const lang = settings.primary;

  return (
    <div className="langbar" role="group" aria-label={t(lang, "languageSettings")}>
      <label className="langbar__item">
        <span className="langbar__label">{t(lang, "primaryLanguage")}</span>
        <select
          value={settings.primary}
          onChange={(e) => {
            const next = e.target.value as Lang;
            onChange({
              primary: next,
              // Never show the same language twice.
              secondary: settings.secondary === next ? settings.primary : settings.secondary,
            });
          }}
        >
          {LANGS.map((l) => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>
      </label>

      <button
        type="button"
        className="langbar__swap"
        onClick={onSwap}
        disabled={!settings.secondary}
        title={`${t(lang, "scSwapLang")} (S)`}
        aria-label={t(lang, "scSwapLang")}
      >
        ⇄
      </button>

      <label className="langbar__item">
        <span className="langbar__label">{t(lang, "secondaryLanguage")}</span>
        <select
          value={settings.secondary ?? ""}
          onChange={(e) =>
            onChange({ secondary: e.target.value ? (e.target.value as Lang) : null })
          }
        >
          <option value="">— {t(lang, "secondaryOff")} —</option>
          {LANGS.filter((l) => l.code !== settings.primary).map((l) => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>
      </label>
    </div>
  );
}
